<app-page-layout 
  [title]="title"
  [subtitle]="subtitle"
  [phoneNumber]="phoneNumber"
  [heroImage]="heroImage"
  [breadcrumbs]="breadcrumbs"
  [prevPage]="prevPage"
  [nextPage]="nextPage">

  <!-- Main Content Section -->
  <div class="content-wrapper">
    
    <!-- Section: Assurance Responsabilité Civile -->
    <section class="main-section">
      <h2 class="section-title">Assurance Responsabilité Civile</h2>
      
      <div class="intro-block">
        <h3 class="subsection-title">Place de la RC PRO dans les assurances Entreprise :</h3>
        <p>Il existe 4 grandes familles de risque auxquels sont exposées les entreprises et dans lesquelles fuse en bonne place l'assurance responsabilité civile :</p>
        
        <div class="risk-list">
          <div class="risk-item">
            <span class="risk-number">1.</span>
            <div class="risk-content">
              <strong>Les risques de dommages aux biens</strong> (Protéger ce que vous possédez) :
              <ul>
                <li>Locaux,</li>
                <li>Matériels,</li>
                <li>Mobiliers,</li>
                <li>Marchandises.</li>
              </ul>
            </div>
          </div>

          <div class="risk-item">
            <span class="risk-number">2.</span>
            <div class="risk-content">
              <strong>Les risques de responsabilité</strong> (Protéger les autres dommages que « vous » causez) :
              <ul>
                <li>Responsabilité encourue vis à vis des tiers du fait de votre exploitation (un client se blesse dans votre local, à cause d'une anomalie de votre matériel),</li>
                <li>Responsabilité encourue du fait des prestations (ou des produits) vendus, fabriqués ou distribués (les conseils ne sont pas bons, le gâteau que vous vendez intoxique quelqu'un)</li>
                <li>Responsabilité de votre fait personnel (faute inexcusable de l'employeur) ou de vos ressources humaines (faute intentionnelle d'un employeur sur un tiers).</li>
              </ul>
            </div>
          </div>

          <div class="risk-item">
            <span class="risk-number">3.</span>
            <div class="risk-content">
              <strong>Les risques financiers</strong> (Protéger la dimension pécuniaire de votre entreprise) :
              <ul>
                <li>Perte d'exploitation,</li>
                <li>Perte de valeur vénale du fonds de commerce,</li>
                <li>Risque de perte de créance sur les clients.</li>
              </ul>
            </div>
          </div>

          <div class="risk-item">
            <span class="risk-number">4.</span>
            <div class="risk-content">
              <strong>Les risques de personnes</strong> (Protéger vos ressources humaines) :
              <ul>
                <li>Dommages matériels ou corporels subis par votre personnel sédentaire (santé, invalidité, décès),</li>
                <li>Risques encourus par votre personnel non sédentaire ou risques encourus par vos collaborateurs en mission à l'étranger.</li>
              </ul>
            </div>
          </div>
        </div>

        <p class="note-text">
          L'assurance RC PRO est souvent associée à deux autres contrats d'assurance sous forme d'un package permettant une protection 360° de la responsabilité de l'entreprise et du dirigeant :
          Il s'agit de l'<a routerLink="/assurance-protection-juridique-pro" class="link-purple">Assurance Protection Juridique Professionnelle</a> (PJ PRO) et de l'<a routerLink="/assurance-rc-mandataires-sociaux" class="link-purple">Assurance Responsabilité Civile Mandataires Sociaux</a> (RCMS).
        </p>

        <p class="note-text">
          Le courtier en assurance BROKINS est partenaire de nombreux assureurs, dont un échantillon est listé ci-après : <strong>GENERALI, AXA SPVIE, PROGEAS, HELVETIA, CREDIT MUTUEL, SOLLY AZAR, HELVETIA, SWISSLIFE, NEXUS.</strong>
        </p>
      </div>
    </section>

    <!-- Section: La Responsabilité Civile dans une Entreprise -->
    <section class="main-section highlighted-section">
      <h2 class="section-title purple-title">La Responsabilité Civile dans une Entreprise :</h2>
      
      <p class="italic-intro">
        Je me mets en responsabilité civile pour répondre devant la loi, à l'obligation suivante : <strong class="purple-text">Je dois réparer le dommage que je cause à autrui.</strong>
      </p>

      <ul class="bullet-list">
        <li>Si j'ai souscrit une assurance Responsabilité Civile, c'est l'assureur qui "répare".</li>
        <li>Si je ne suis pas assuré, c'est moi mon entreprise qui doit réparer.</li>
      </ul>

      <p>
        Souscrire à une assurance RC PRO permet de protéger financièrement votre entreprise comme une <a routerLink="/assurance-prevoyance-entreprise" class="link-purple">Assurance Prévoyance Entreprise</a> protégerait financièrement vos salariés.
      </p>

      <p>
        Donc la diminution du patrimoine d'une entreprise peut être indirecte mais non moins réelle : elle peut par exemple résulter de l'obligation qui lui est faite de réparer un préjudice subi par un tiers (client, fournisseur, visiteur, État…), lorsqu'il est lui même tenu pour responsable de ce préjudice. L'assurance de responsabilité évite donc bien l'appauvrissement de l'assuré, puisque l'assureur se substitue à lui pour dédommager le tiers lésé.
      </p>

      <p>
        À ce jour, la Responsabilité Civile Professionnelle n'est obligatoire que pour les professions réglementées, dont vous trouverez une liste ci-dessous :
      </p>

      <div class="professions-grid">
        <ul>
          <li>agences de voyages</li>
          <li>agents d'affaires</li>
          <li>agents immobiliers</li>
          <li>architectes</li>
          <li>artisan du bâtiment</li>
          <li>associations sportives d'amateurs</li>
          <li>auto-écoles</li>
          <li>avocats</li>
          <li>chasse</li>
          <li>commissaires aux comptes</li>
          <li>courtier d'assurance</li>
          <li>courtier en crédit</li>
          <li>conseiller en investissement financier</li>
          <li>bureau d'étude dans le bâtiment</li>
          <li>entraide agricole</li>
          <li>établissements d'éducation physique</li>
        </ul>
        <ul>
          <li>établissements de vacances</li>
          <li>exploitants agricoles (accidents d'assurance)</li>
          <li>dommage ouvrage</li>
          <li>experts-comptables</li>
          <li>exploitants de funiculaires, téléphériques et remonte-pentes</li>
          <li>géomètres experts</li>
          <li>gérants d'immeubles</li>
          <li>locatiers</li>
          <li>manifestations sportives</li>
          <li>manifestations sportives aériennes</li>
          <li>pêcheurs sous-marins</li>
          <li>professions de danse</li>
          <li>professions médicales et établissements de soins</li>
          <li>véhicules terrestres à moteur</li>
          <li>syndicats de copropriété</li>
        </ul>
      </div>

      <p class="note-text">
        Si votre profession n'apparaît pas dans cette liste, c'est que votre assurance RC PRO n'est pas obligatoire, mais cette assurance peut être exigée par vos clients lors de certains majeurs...
        mais pas que.
      </p>

      <p class="note-text">
        Le code des assurances, en son article L 112 et son annexe A 112, prévoit deux possibilités de prise en charge de l'assureur d'un sinistre de responsabilité civile :
      </p>

      <ul class="bullet-list">
        <li>soit le déclenchement par le fait dommageable</li>
        <li>soit le déclenchement par la réclamation</li>
      </ul>

      <p class="note-text">
        Attention à la couverture géographique de cette assurance : L'assurance responsabilité civile est internationale à l'exception des USA et du Canada si l'assurance a été souscrite en France, à Monaco, ou Andorre. Demeurent toutefois garantis les dommages causés à l'occasion de séjours de l'assuré et/ou de ses représentants pour des missions d'études, des missions commerciales, la participation à des congrès, séminaires, colloques, foires, salons ou expositions sur un même territoire limités à la mesure où la durée du séjour dans ces pays n'excède pas une durée délimitée au contrat (généralement de trois à six mois).
      </p>
    </section>

    <!-- Section: Pourquoi souscrire -->
    <section class="main-section">
      <h2 class="section-title">J'ai une entreprise, pourquoi devrais-je souscrire une assurance responsabilité civile ?</h2>
      
      <div class="reasons-list">
        <div class="reason-item">
          <span class="reason-number">1.</span>
          <div class="reason-content">
            <strong>Pour m'assurer contre les dommages causés à des tiers par mon activité</strong> (lieu d'exploitation de la société, c'est-à-dire lieu où s'exercent ses activités) : RC EXTRA-CONTRACTUELLE dite RC EXPLOITATION :
            <ul>
              <li>Du fait du chef d'entreprise,</li>
              <li>Du fait des préposés de l'entreprise,</li>
              <li>Du fait des locaux, des matériels et installations,</li>
              <li>Du fait des animaux,</li>
              <li>Du fait de la ruine des immeubles,</li>
              <li>Incendie dans les locaux de l'entreprise (uniquement dommages corporels),</li>
              <li>Incendie hors des locaux de l'entreprise,</li>
              <li>Du fait des activités hors locaux de l'entreprise,</li>
              <li>Pollution d'origine accidentelle,</li>
              <li>Dommages causés par les véhicules des préposés utilisés pour les besoins de l'entreprise,</li>
              <li>Travaux réalisés en qualité de maître d'ouvrage,</li>
              <li>Dommages causés par vice ou recel militaires,</li>
              <li>Véhicules appartenant à des tiers déplacés par vos préposés pour les besoins de l'entreprise,</li>
              <li>Dommages matériels causés aux préposés de l'entreprise.</li>
            </ul>
          </div>
        </div>

        <div class="reason-item">
          <span class="reason-number">2.</span>
          <div class="reason-content">
            <strong>Pour m'assurer contre les dommages causés au titre de ma responsabilité contractuelle</strong> : RC CONTRACTUELLE dite RC APRÈS LIVRAISON ou RC PRODUIT ou RC PROFESSIONNELLE :
            <ul>
              <li>Dommages causés par les produits livrés,</li>
              <li>Préjudices causés du fait des services vendus,</li>
              <li>Dommages causés par les travaux réceptionnés (sauf ceux aux ouvrages de bâtiment réalisés),</li>
              <li>Dommages subis par les ouvrages de bâtiment réceptionnés (RC décennale),</li>
              <li>Dommages divers sauf incendie & risques annexes subis par les biens confiés par les clients dans les locaux de l'entreprise,</li>
              <li>Tous dommages subis par les biens confiés par les clients hors des locaux de l'entreprise,</li>
              <li>Dommages aux effets et bagages des voyageurs (RC autocariste et hôtelier),</li>
              <li>Dommages subis par les ouvrages de bâtiment non réceptionnés (extension RC Exploitation),</li>
              <li>Autres dommages dont la responsabilité incombe à l'entreprise du fait d'obligations envers les organismes publics ou para-publics.</li>
            </ul>
          </div>
        </div>

        <div class="reason-item">
          <span class="reason-number">3.</span>
          <div class="reason-content">
            <strong>Pour m'assurer contre les dommages causés au personnel salarié</strong> : RC DES MANDATAIRES SOCIAUX OU DIRIGEANTS :
            <ul>
              <li>Faute inexcusable de l'employeur,</li>
              <li>Faute intentionnelle d'un co-préposé,</li>
              <li>Accident de trajet d'un salarié (hors dommages corporels),</li>
              <li>Dommages matériels subis par les préposés (pour les seuls dommages matériels),</li>
              <li>Dommages subis par les stagiaires ou nouveaux salariés travaillant moins de 120 heures de présence dans l'entreprise,</li>
              <li>Dommages causés par les véhicules des préposés utilisés pour les besoins du service.</li>
            </ul>
          </div>
        </div>
      </div>
    </section>

  </div>

</app-page-layout>
